import { useEffect, useState } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { Building2, Code2, CreditCard, Home, LayoutDashboard, LifeBuoy, LogOut, Search, Users } from "lucide-react";
import { setToken } from "@/lib/api";
import { cn, initials } from "@/lib/utils";
import { useAgent } from "@/hooks/useAgent";
import { Page } from "@/components/shared/ui";
import { SearchPalette, useSearchShortcut } from "@/components/agent/SearchPalette";

const SECTIONS = [
  { items: [
    { to: "/", label: "Overview", icon: LayoutDashboard, testid: "nav-overview" },
    { to: "/applicants", label: "Applicants", icon: Users, testid: "nav-applicants" },
    { to: "/properties", label: "Properties", icon: Home, testid: "nav-properties" },
  ] },
  { title: "Business", items: [
    { to: "/organization", label: "Organization", icon: Building2, testid: "nav-organization" },
    { to: "/billing", label: "Billing", icon: CreditCard, testid: "nav-billing" },
  ] },
];

const FOOTER = [
  { to: "/developers", label: "Developers", icon: Code2, testid: "nav-developers" },
  { to: "/help", label: "Help", icon: LifeBuoy, testid: "nav-help" },
];

const navCls = ({ isActive }) =>
  cn("flex h-9 shrink-0 items-center gap-3 rounded-lg px-3 text-sm transition-colors",
    isActive ? "bg-sidebar-accent font-medium text-foreground" : "text-foreground/75 hover:bg-sidebar-accent hover:text-foreground");

const isMac = typeof navigator !== "undefined" && /Mac|iPhone|iPad/.test(navigator.platform);

export const LogoMark = ({ className }) => (
  <img src={`${process.env.PUBLIC_URL}/logo192.png`} alt="" className={cn("size-7 shrink-0 rounded-lg", className)} />
);

export const Brand = () => (
  <span className="flex items-center gap-2" data-testid="landin-brand">
    <LogoMark />
    <span className="text-sm font-semibold">Landin</span>
  </span>
);

/** On phones the sidebar is one scrolling row, so section titles and the search bar only show from md up. */
const NavGroup = ({ title, items }) => (
  <div className="contents md:flex md:flex-col md:gap-1">
    {title && <p className="hidden px-3 pb-1 text-xs font-medium text-muted-foreground md:block">{title}</p>}
    {items.map(({ to, label, icon: Icon, testid }) => (
      <NavLink key={to} to={to} end={to === "/"} className={navCls} data-testid={testid}>
        <Icon className="size-4" /> {label}
      </NavLink>
    ))}
  </div>
);

const SearchButton = ({ onClick }) => (
  <button type="button" onClick={onClick} data-testid="sidebar-search"
    className="hidden h-9 items-center gap-2.5 rounded-lg border border-sidebar-border bg-background px-3 text-sm text-muted-foreground shadow-xs transition-colors hover:text-foreground md:flex">
    <Search className="size-4" />
    <span className="flex-1 text-left">Search</span>
    <kbd className="rounded border border-sidebar-border px-1.5 font-sans text-[11px] leading-5">{isMac ? "⌘" : "Ctrl "}K</kbd>
  </button>
);

const Sidebar = () => {
  const navigate = useNavigate();
  const agent = useAgent();
  const [searching, setSearching] = useState(false);
  useSearchShortcut(setSearching);
  const logout = () => {
    setToken(null);
    navigate("/login", { replace: true });
  };
  return (
    <aside className="flex w-full flex-col gap-2 border-b border-sidebar-border bg-sidebar p-2 md:fixed md:inset-y-0 md:left-0 md:w-60 md:gap-0 md:border-b-0 md:border-r md:px-3 md:py-4" data-testid="agent-rail">
      <Link to="/" className="flex h-10 w-fit items-center rounded-lg px-2 md:mb-4" title="Overview" data-testid="brand-home"><Brand /></Link>
      <SearchButton onClick={() => setSearching(true)} />
      <nav className="flex gap-1 overflow-x-auto md:mt-6 md:min-h-0 md:flex-1 md:flex-col md:gap-7 md:overflow-y-auto">
        {SECTIONS.map((s, i) => <NavGroup key={i} {...s} />)}
        <div className="contents md:mt-auto md:flex md:flex-col">
          <NavGroup items={FOOTER} />
        </div>
      </nav>
      <div className="md:mt-3 md:border-t md:border-sidebar-border md:pt-3">
        <div className="flex items-center gap-1">
          <NavLink to="/settings" title="Account settings" data-testid="nav-account"
            className={({ isActive }) => cn("flex min-w-0 flex-1 items-center gap-2.5 rounded-lg p-1.5 transition-colors hover:bg-sidebar-accent", isActive && "bg-sidebar-accent")}>
            <span className="grid size-8 shrink-0 place-items-center rounded-full bg-foreground text-xs font-medium text-background">{initials(agent?.name || agent?.email || "")}</span>
            <span className="min-w-0">
              <span className="block text-sm font-medium leading-tight [overflow-wrap:anywhere]" data-testid="agent-name">{agent?.name}</span>
              <span className="block text-xs leading-tight text-muted-foreground [overflow-wrap:anywhere]">{agent?.email}</span>
            </span>
          </NavLink>
          <button type="button" onClick={logout} aria-label="Sign out" title="Sign out" data-testid="logout-button"
            className="grid size-8 shrink-0 place-items-center rounded-md text-muted-foreground hover:bg-sidebar-accent hover:text-foreground">
            <LogOut className="size-4" />
          </button>
        </div>
      </div>
      <SearchPalette open={searching} onOpenChange={setSearching} />
    </aside>
  );
};

/** A link ending in #id (from search) scrolls to that section once the page has loaded it, then briefly rings it. */
const useHashScroll = () => {
  const { pathname, hash } = useLocation();
  useEffect(() => {
    if (!hash) return;
    const id = decodeURIComponent(hash.slice(1));
    let tries = 0;
    const timer = setInterval(() => {
      const el = document.getElementById(id);
      if (!el && ++tries < 40) return;
      clearInterval(timer);
      if (!el) return;
      el.style.scrollMarginTop = "24px";
      el.scrollIntoView({ behavior: "smooth", block: "start" });
      const timing = { duration: 1600, easing: "ease-out" };
      if (el.tagName === "SECTION" && el.lastElementChild) {
        const ring = "0 0 0 2px hsl(var(--brand-strong))";
        el.lastElementChild.animate([{ boxShadow: ring }, { boxShadow: ring, offset: 0.6 }, { boxShadow: "0 0 0 2px transparent" }], timing);
      } else {
        const tint = "hsl(var(--brand) / 0.35)";
        el.animate([{ backgroundColor: tint }, { backgroundColor: tint, offset: 0.6 }, { backgroundColor: "transparent" }], timing);
      }
    }, 75);
    return () => clearInterval(timer);
  }, [pathname, hash]);
};

export const AgentLayout = ({ children, wide }) => {
  useHashScroll();
  return (
    <div className="min-h-screen bg-background text-sm text-foreground">
      <Sidebar />
      <main className="min-h-screen md:pl-60"><Page wide={wide}>{children}</Page></main>
    </div>
  );
};
